import { Col, Form, Row, Select } from 'antd5';
import { useForm } from 'antd5/es/form/Form';
import { difference } from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlMetadataField,
  AtlasGqlMetadataFieldMapping,
  AtlasGqlFileFormat,
} from 'types/atlas-graphql';
import {
  ButtonContainer,
  ConfigurationText,
  LabelColumn,
} from '../UploadInspectionDataModal.style';
import { Button } from 'components/ui';
import { Steps } from '../UploadInspectionDataModal';

type FieldMappingProps = {
  fieldMappings?: AtlasGqlMetadataFieldMapping[];
  fileType: AtlasGqlFileFormat;
  ignoreRequired?: boolean;
  internalFields: AtlasGqlMetadataField[];
  /**
   * If provided, form will error and not submit unless a mapping for at least one of the
   * field names in each set is made; each array is a set of independent requirements
   *
   * e.g. [['id', 'taskId']]
   */
  requireAtLeastOneOf?: string[][];
  providedFieldNames: string[];
  setFieldMappings: Dispatch<SetStateAction<AtlasGqlMetadataFieldMapping[] | undefined>>;
  previousStep: Steps;
  nextStep: Steps;
  setStep: Dispatch<SetStateAction<Steps>>;
  likelyDelimiterError: boolean;
  cellDelimiter: AtlasGqlFileFieldDelimiter;
  setCellDelimiter: Dispatch<SetStateAction<AtlasGqlFileFieldDelimiter>>;
  decimalDelimiter: AtlasGqlDecimalSeparator;
  setDecimalDelimiter: Dispatch<SetStateAction<AtlasGqlDecimalSeparator>>;
};

export const FieldMappingForm: React.FC<FieldMappingProps> = ({
  fieldMappings,
  fileType,
  ignoreRequired,
  internalFields,
  requireAtLeastOneOf,
  providedFieldNames,
  setFieldMappings,
  previousStep,
  nextStep,
  setStep,
  likelyDelimiterError,
  cellDelimiter,
  setCellDelimiter,
  decimalDelimiter,
  setDecimalDelimiter,
}) => {
  const [form] = useForm<{ [key: string]: string }>();
  const [usedFieldNames, setUsedFieldNames] = useState<string[]>([]);
  const hasErrors = form.getFieldsError().some(({ errors }) => !!errors.length);

  /**
   * Initialize form with any existing values (if user has gone back and forth) or
   * any field names that match the system defaults
   */
  const initialValues = useMemo(() => {
    if (fieldMappings) {
      return Object.fromEntries(
        fieldMappings.map(({ sourceFieldName, targetFieldName }) => [
          targetFieldName,
          sourceFieldName,
        ])
      );
    }

    return Object.fromEntries(
      internalFields.map(field => [
        field.internalName,
        providedFieldNames.find(name =>
          [field.defaultCsvHeader, field.defaultJsonFieldName].includes(name)
        ),
      ])
    );
  }, [fieldMappings, internalFields, providedFieldNames, cellDelimiter]);

  useEffect(() => {
    form.resetFields();
  }, [initialValues]);

  const availableFieldOptions = useMemo(
    () =>
      likelyDelimiterError
        ? []
        : difference(providedFieldNames, usedFieldNames).map((h: string) => ({
            label: h,
            value: h,
          })),
    [providedFieldNames, usedFieldNames, likelyDelimiterError]
  );

  useEffect(() => {
    setUsedFieldNames(Object.values(initialValues).filter((name): name is string => !!name));
  }, [initialValues]);

  const handleSelect = () => {
    const values = form.getFieldsValue();
    setUsedFieldNames(Object.values(values).filter((name): name is string => !!name));
  };

  const handleSubmit = (values: { [key: string]: string }) => {
    form.resetFields();
    setFieldMappings(
      Object.entries(values)
        .map(([targetFieldName, sourceFieldName]) => ({
          sourceFieldName,
          targetFieldName,
        }))
        .filter((m): m is AtlasGqlMetadataFieldMapping => !!m.sourceFieldName)
    );
    setStep(nextStep);
  };

  const values = form.getFieldsValue();
  const requirementMessages = useMemo(() => {
    if (requireAtLeastOneOf?.length) {
      return requireAtLeastOneOf.reduce<{ [key: string]: string }>((messages, requiredFieldSet) => {
        const displayNames = requiredFieldSet.map(
          internalName =>
            internalFields.find(f => internalName === f.internalName)?.displayName ?? internalName
        );
        const message = `${displayNames.join(', ')} cannot ${displayNames.length === 2 ? 'both' : 'all'} be empty`;
        return {
          ...messages,
          ...Object.fromEntries(requiredFieldSet.map(field => [field, message])),
        };
      }, {});
    }
    return {};
  }, [requireAtLeastOneOf]);
  const requirementsSatisfied = useMemo(() => {
    if (requireAtLeastOneOf?.length) {
      return requireAtLeastOneOf
        .map(requiredFieldSet =>
          Object.entries(values).some(
            ([targetFieldName, sourceFieldName]) =>
              requiredFieldSet.includes(targetFieldName) && !!sourceFieldName
          )
        )
        .every(satisfied => satisfied);
    }
    return true;
  }, [requireAtLeastOneOf, values]);

  const handleCancel = () => {
    form.resetFields();
    setFieldMappings(undefined);
    setStep(previousStep);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      scrollToFirstError={true}
      initialValues={initialValues}
    >
      <Row gutter={{ sm: 16 }}>
        <Col flex="50%">
          {internalFields.map(field => (
            <Row key={field.id} justify={'space-between'}>
              <LabelColumn flex="auto" paddingTop="0.25rem">
                <label>
                  {field.displayName}
                  {field.required && !ignoreRequired && <span>*</span>}:
                </label>
              </LabelColumn>
              <Col flex="55%">
                <Form.Item
                  key={field.internalName}
                  name={field.internalName}
                  rules={[
                    {
                      required: (field.required && !ignoreRequired) ?? false,
                      message: `${field.displayName ?? field.internalName} cannot be empty`,
                      whitespace: true,
                    },
                  ]}
                  {...(requirementMessages[field.internalName] && !requirementsSatisfied
                    ? {
                        validateStatus: 'error',
                        help: requirementMessages[field.internalName],
                        style: { marginBottom: '0px' },
                      }
                    : {})}
                >
                  <Select
                    data-testid={`inspection-mapping-field-${field.internalName}`}
                    placeholder={`Select source ${fileType === AtlasGqlFileFormat.Csv ? 'column' : 'field'}`}
                    allowClear
                    options={availableFieldOptions}
                    onChange={handleSelect}
                  />
                </Form.Item>
              </Col>
            </Row>
          ))}
        </Col>
        <Col flex="50%">
          <ConfigurationText>
            Configure Delimiters
            {likelyDelimiterError && (
              <p className="warning-text">
                It appears your csv has delimiters other than {cellDelimiter.toLowerCase()}s. Please
                select the appropriate delimiter before mapping fields.
              </p>
            )}
          </ConfigurationText>
          {fileType === AtlasGqlFileFormat.Csv && (
            <Row key={'cellDelimiter'} justify={'space-between'}>
              <LabelColumn flex="auto" paddingTop="0.25rem">
                <label>Cell Delimiter:</label>
              </LabelColumn>
              <Col flex="60%">
                <Form.Item>
                  <Select
                    data-testid={`inspection-mapping-cell-delimiter`}
                    allowClear={false}
                    value={cellDelimiter}
                    onChange={value => {
                      form.resetFields();
                      setCellDelimiter(value);
                    }}
                    options={[
                      {
                        label: 'Comma - Default',
                        value: AtlasGqlFileFieldDelimiter.Comma,
                        disabled: decimalDelimiter === AtlasGqlDecimalSeparator.Comma,
                      },
                      {
                        label: 'Semicolon',
                        value: AtlasGqlFileFieldDelimiter.Semicolon,
                      },
                      {
                        label: 'Tab',
                        value: AtlasGqlFileFieldDelimiter.Tab,
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row key={'decimalDelimiter'} justify={'space-between'}>
            <LabelColumn flex="auto" paddingTop="0.25rem">
              <label>Decimal Delimiter:</label>
            </LabelColumn>
            <Col flex="60%">
              <Form.Item>
                <Select
                  data-testid={`inspection-mapping-decimal-delimiter`}
                  allowClear={false}
                  value={decimalDelimiter}
                  onChange={setDecimalDelimiter}
                  options={[
                    {
                      label: 'Period - Default',
                      value: AtlasGqlDecimalSeparator.Period,
                    },
                    {
                      label: 'Comma',
                      value: AtlasGqlDecimalSeparator.Comma,
                      disabled:
                        fileType === AtlasGqlFileFormat.Csv &&
                        cellDelimiter === AtlasGqlFileFieldDelimiter.Comma,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <ButtonContainer>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          type="primary"
          htmlType="submit"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            form.submit();
          }}
          disabled={hasErrors || !requirementsSatisfied}
        >
          Preview
        </Button>
      </ButtonContainer>
    </Form>
  );
};
